import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Group, GroupPayload } from "./types";
import {
  settingsQueryKey,
  useSettings,
  putGroup,
  testGroup,
  fetchSchema,
} from "./api";
import { FormPanel } from "./panels/FormPanel";
import { AboutPanel } from "./panels/AboutPanel";
import { CollectionPanel } from "./panels/CollectionPanel";
import { CustomPanelHost, type CustomRegistry } from "./panels/CustomPanelHost";
import { VersionBadge } from "./VersionBadge";

interface Props {
  customRegistry?: CustomRegistry;
}

// Top-level category for the settings nav. Keys map to a stable set
// of buckets so URL bookmarks (?cat=crypto) survive group renames;
// labels render in the top nav and order their column from left to
// right. Single-tier nav with 5 buckets — splitting the previous big
// "주식" lump into 한국주식 / 미국주식 / 뉴스분석 makes finding any
// one setting (e.g. trading.max_per_ticker_pct) one click instead of
// a scroll through 10+ group panels.
type Category =
  | "stock"
  | "us_stock"
  | "news"
  | "crypto"
  | "common";

type CategoryDef = {
  key: Category;
  label: string;
};

const CATEGORIES: CategoryDef[] = [
  // "stock" key kept (not "kr_stock") so existing bookmarks survive.
  { key: "stock", label: "📈 한국주식" },
  { key: "us_stock", label: "🇺🇸 미국주식" },
  { key: "news", label: "📰 뉴스분석" },
  { key: "crypto", label: "🪙 암호화폐" },
  { key: "common", label: "⚙️ 공통" },
];

const CATEGORY_KEYS: Set<Category> = new Set(CATEGORIES.map((c) => c.key));

// categoryOf classifies a group key into one of the top buckets.
// Single-purpose helper so adding a new settings group only requires
// adding a prefix (or a literal key) here — the SettingsPage itself
// stays generic. Anything that doesn't match falls through to
// "common" so a freshly-added group is always reachable.
function categoryOf(key: string): Category {
  const k = key.toLowerCase();

  // Crypto stack: Bithumb + crypto.* trading + risk + session groups.
  if (k.startsWith("crypto") || k.startsWith("bithumb")) return "crypto";

  // News pipeline — collector, cluster, summariser tunables. Read by
  // both KR + US stock paths but operationally owned by one team.
  const newsGroups = new Set([
    "news",
    "news_cluster_phase3",
    "news_cluster",
    "news_summary",
    "trend_collector",
  ]);
  if (newsGroups.has(k) || k.startsWith("news_")) return "news";

  // US market stack — equity universe + overseas KIS endpoints. The
  // backtest/suggester for US tickers also lands here if/when it
  // ships separately from the KR variant.
  const usGroups = new Set([
    "us_equity",
    "overseas",
    "nasdaq",
  ]);
  if (usGroups.has(k) || k.startsWith("us_") || k.startsWith("overseas")) {
    return "us_stock";
  }

  // KR stack: KIS broker, trading params, signals reducer, session
  // orchestrator + suggester/backtest (KR-side).
  const krStockGroups = new Set([
    "trading",
    "signals",
    "session",
    "kis_broker",
    "suggest",
    "backtest",
  ]);
  if (krStockGroups.has(k)) return "stock";

  // LLM + embeddings + notifications + about + integrations live in
  // common because both market stacks consume them.
  return "common";
}

// useActiveCategory mirrors useActiveTab — survives reload + back
// button via ?cat= query string. Falls back to the first category
// that has at least one group so a fresh visit lands somewhere
// useful instead of an empty "stock" pane on a crypto-only deploy.
function useActiveCategory(groups: Group[] | undefined): [Category, (k: Category) => void] {
  const [active, setActive] = useState<Category>(() => {
    if (typeof window === "undefined") return "stock";
    const raw = new URLSearchParams(window.location.search).get("cat");
    if (raw && CATEGORY_KEYS.has(raw as Category)) return raw as Category;
    return "stock";
  });

  useEffect(() => {
    if (!groups || groups.length === 0) return;
    const hasAny = groups.some((g) => categoryOf(g.key) === active);
    if (!hasAny) {
      const fallback = CATEGORIES.find((c) =>
        groups.some((g) => categoryOf(g.key) === c.key),
      );
      if (fallback) setActive(fallback.key);
    }
  }, [groups, active]);

  const update = (k: Category) => {
    setActive(k);
    if (typeof window !== "undefined") {
      const u = new URL(window.location.href);
      u.searchParams.set("cat", k);
      // Reset the sub-tab when switching categories so we don't end
      // up pointing at a group that lives in the other bucket.
      u.searchParams.delete("tab");
      window.history.replaceState({}, "", u.toString());
    }
  };

  return [active, update];
}

/** Reads / writes the active tab key to the URL query string so refresh &
 *  bookmarking work. Falls back to lowest-order group on first visit. */
function useActiveTab(groups: Group[] | undefined): [string, (k: string) => void] {
  const [active, setActive] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("tab") ?? "";
  });

  useEffect(() => {
    if (!groups || groups.length === 0) return;
    const valid = groups.some((g) => g.key === active);
    if (!valid) setActive(groups[0].key);
  }, [groups, active]);

  const update = (k: string) => {
    setActive(k);
    if (typeof window !== "undefined") {
      const u = new URL(window.location.href);
      u.searchParams.set("tab", k);
      window.history.replaceState({}, "", u.toString());
    }
  };

  return [active, update];
}

export function SettingsPage({ customRegistry = {} }: Props) {
  const schema = useQuery({
    queryKey: ["settings-schema"],
    queryFn: fetchSchema,
    staleTime: 5 * 60 * 1000,
  });

  const groups = schema.data?.groups;
  const [activeCategory, setActiveCategory] = useActiveCategory(groups);

  // Groups filtered to the currently-selected top category. useActiveTab
  // operates on this slice so the sub-tab state is naturally scoped per
  // category — e.g. switching from "📈 주식" to "🪙 암호화폐" lands on
  // the first crypto group, not whatever stock tab was active before.
  const visibleGroups = useMemo(
    () => groups?.filter((g) => categoryOf(g.key) === activeCategory),
    [groups, activeCategory],
  );

  const [active, setActive] = useActiveTab(visibleGroups);

  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});

  const switchActive = useCallback(
    (nextKey: string) => {
      setDirtyMap((prev) => {
        const next = { ...prev };
        delete next[active];
        return next;
      });
      setActive(nextKey);
    },
    [active, setActive],
  );

  const reportDirty = useCallback(
    (groupKey: string, dirty: boolean) => {
      setDirtyMap((prev) => {
        const current = prev[groupKey] ?? false;
        if (current === dirty) return prev;
        if (!dirty) {
          const next = { ...prev };
          delete next[groupKey];
          return next;
        }
        return { ...prev, [groupKey]: true };
      });
    },
    [],
  );

  const activeGroup = useMemo(
    () => visibleGroups?.find((g) => g.key === active),
    [visibleGroups, active],
  );

  // Per-category dirty indicator so a user navigating to "암호화폐"
  // while leaving an unsaved change in "주식" still sees the warning
  // dot on the stock tab.
  const dirtyByCategory = useMemo(() => {
    const out = {} as Record<Category, boolean>;
    for (const c of CATEGORIES) out[c.key] = false;
    if (!groups) return out;
    for (const g of groups) {
      if (dirtyMap[g.key]) out[categoryOf(g.key)] = true;
    }
    return out;
  }, [groups, dirtyMap]);

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <header className="mb-6 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Settings</h1>
        <VersionBadge />
      </header>

      {schema.isPending && (
        <div className="text-sm text-muted-foreground">Loading schema…</div>
      )}
      {schema.error && (
        <div className="rounded-md border border-destructive-soft bg-destructive-soft p-3 text-sm text-destructive-soft-foreground">
          Failed to load schema: {String(schema.error)}
        </div>
      )}

      {groups && groups.length > 0 && (
        <>
          {/* Top nav — market categories. Renders only the buckets
              that actually have at least one group so an unused
              category (e.g. a crypto-only deploy where the stock
              groups are absent) doesn't show as an empty tab.
              Visually heavier than the sub-tabs below to signal it's
              the higher-level switch. */}
          <nav className="mb-4 flex gap-1 border-b border-border">
            {CATEGORIES.map((cat) => {
              const hasGroups = groups.some((g) => categoryOf(g.key) === cat.key);
              if (!hasGroups) return null;
              const isActive = cat.key === activeCategory;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setActiveCategory(cat.key)}
                  className={`relative -mb-px whitespace-nowrap border-b-2 px-5 py-2.5 text-sm font-semibold transition-colors ${
                    isActive
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                  }`}
                >
                  {cat.label}
                  {dirtyByCategory[cat.key] && (
                    <span className="ml-1 text-warning" aria-label="unsaved changes">
                      ●
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Sub nav — groups within the active category. flex-wrap
              behaves as "one row when it fits, wrap when it doesn't"
              — single-row for small group counts on wide screens,
              multi-row only after the natural width runs out. */}
          {visibleGroups && visibleGroups.length > 0 && (
            <nav className="mb-6 flex flex-wrap gap-x-1 gap-y-0.5">
              {visibleGroups.map((g) => {
                const isActive = g.key === active;
                return (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => switchActive(g.key)}
                    className={`relative whitespace-nowrap border-b px-4 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "border-foreground text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {g.title}
                    {dirtyMap[g.key] && (
                      <span className="ml-1 text-warning" aria-label="unsaved changes">
                        ●
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          )}

          {activeGroup && (
            <PanelSwitch
              group={activeGroup}
              customRegistry={customRegistry}
              onDirtyChange={(d) => reportDirty(activeGroup.key, d)}
            />
          )}
        </>
      )}
    </div>
  );
}

function PanelSwitch({
  group,
  customRegistry,
  onDirtyChange,
}: {
  group: Group;
  customRegistry: CustomRegistry;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const q = useSettings(group.key);
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  if (q.isPending) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading {group.title}…
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="rounded-lg border border-destructive-soft bg-destructive-soft p-6 text-sm text-destructive-soft-foreground">
        Failed to load {group.title}: {String(q.error)}
      </div>
    );
  }
  const payload = q.data as GroupPayload;

  if (payload.kind === "form") {
    return (
      <div className="space-y-3">
        <FormPanel
          group={group}
          payload={payload}
          saving={saving}
          error={error}
          onDirtyChange={onDirtyChange}
          onSave={async (body) => {
            setSaving(true);
            setError(null);
            try {
              const fresh = await putGroup(group.key, body);
              qc.setQueryData(settingsQueryKey(group.key), fresh);
            } catch (e) {
              setError(String(e));
              throw e;
            } finally {
              setSaving(false);
            }
          }}
          onTest={
            group.actions?.includes("test")
              ? async () => {
                  setTestMsg("Testing…");
                  const res = await testGroup(group.key);
                  setTestMsg(
                    res.ok
                      ? `✓ OK (${res.latency_ms ?? "?"}ms)`
                      : `✗ FAIL: ${res.error ?? "unknown"}`,
                  );
                }
              : undefined
          }
        />
        {testMsg && (
          <div
            className={`rounded-md border px-3 py-2 text-xs ${
              testMsg.startsWith("✓")
                ? "border-success-soft bg-success-soft text-success-soft-foreground"
                : testMsg.startsWith("✗")
                ? "border-destructive-soft bg-destructive-soft text-destructive-soft-foreground"
                : "border-border bg-muted text-muted-foreground"
            }`}
          >
            {testMsg}
          </div>
        )}
      </div>
    );
  }
  if (payload.kind === "collection") {
    return <CollectionPanel group={group} payload={payload} />;
  }
  if (payload.kind === "readonly") {
    return <AboutPanel group={group} endpoint={payload.ref.endpoint} />;
  }
  return (
    <CustomPanelHost
      group={group}
      endpoint={payload.ref.endpoint}
      registry={customRegistry}
      onDirtyChange={onDirtyChange}
    />
  );
}
